import {
  DollarOutlined,
  DownOutlined,
  GlobalOutlined,
  LogoutOutlined,
  QrcodeOutlined,
  RightOutlined,
  UserOutlined,
} from "@ant-design/icons";
import { Lang } from "@axl/membership-sdk";
import { Drawer, Dropdown, Select } from "antd";
import cn from "classnames";
import { ReactNode, useState } from "react";
import { useNavigate } from "react-router-dom";

import { useAuth, useLang, useLoc } from "@app/shared/lib/hooks";
import { useDevice } from "@app/shared/providers/DeviceProvider";
import { useTheme } from "@app/shared/providers/ThemeProvider";
import { Loc } from "@app/shared/ui/Loc";
import { Price } from "@app/shared/ui/Price";
import { useUIAppState } from "@app/shared/ui/UIAppState/UIAppStateProvider";
import { UserAvatar } from "@app/shared/ui/UserAvatar";

import { ThemeSwitch } from "./ThemeSwitch";
import type { NavItem } from "./useNavItems";

import styles from "./AppShell.module.scss";

type RowProps = { icon: ReactNode; label: ReactNode; onClick: () => void; danger?: boolean; testId?: string };

const Row: React.FC<RowProps> = ({ icon, label, onClick, danger, testId }) => (
  <button type="button" className={cn(styles.menuRow, danger && styles.menuRowDanger)} onClick={onClick} data-testid={testId}>
    <span className={styles.menuRowIcon} aria-hidden>
      {icon}
    </span>
    <span className={styles.menuRowLabel}>{label}</span>
    {!danger && <RightOutlined className={styles.menuRowChevron} aria-hidden />}
  </button>
);

type PanelProps = {
  /** Sections that are not in the visible navigation (mobile: everything outside the bottom bar). */
  extraItems?: NavItem[];
  onDone: () => void;
};

/** Account card + settings: shared by the desktop dropdown and the mobile bottom sheet. */
export const AccountPanel: React.FC<PanelProps> = ({ extraItems = [], onDone }) => {
  const loc = useLoc();
  // the school may fix the theme (portal design theme.allowSwitch = false): no switch then
  const { allowSwitch } = useTheme();
  // phones: the labelled switch here besides the app-bar ThemeMenuButton
  const { deviceSize } = useDevice();
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const { lang, selectLang } = useLang();
  const { setIsProfileDrawerVisible } = useUIAppState();

  const done = (fn: () => void) => () => {
    onDone();
    fn();
  };

  return (
    <div className={styles.accountPanel}>
      <div className={styles.accountCard}>
        {user && <UserAvatar user={user} size={48} color="var(--axl-primary)" />}
        <div className="overflow-hidden">
          <div className={styles.accountName}>{user?.fullName}</div>
          <div className={styles.accountEmail}>{user?.email}</div>
        </div>
      </div>

      <div className={styles.menuGroup}>
        <Row
          icon={<UserOutlined />}
          label={loc.word("Profile")}
          onClick={done(() => setIsProfileDrawerVisible(true))}
        />
        {extraItems.map((item) => (
          <Row
            key={item.key}
            icon={item.icon}
            label={item.label}
            // the school's own links (portal design nav.items) open the other site in a new tab
            onClick={done(() =>
              item.external ? window.open(item.path, "_blank", "noopener,noreferrer") : navigate(item.path),
            )}
          />
        ))}
        <Row
          icon={<DollarOutlined />}
          label={
            <span className="flex justify-between gap-10 w-100">
              <Loc word="Contact.bonusBalance" />
              {!!user?.bonusBalance && <Price price={user.bonusBalance} className="color-success" />}
            </span>
          }
          onClick={done(() => setIsProfileDrawerVisible(true, "bonus"))}
        />
        <Row
          icon={<QrcodeOutlined />}
          label={<Loc word="QRCode.title" default="QR Code for login" />}
          onClick={done(() => setIsProfileDrawerVisible(true, "qrcode"))}
        />
      </div>

      <div className={styles.menuGroup}>
        <div className={styles.menuSetting}>
          <span className={styles.menuRowIcon} aria-hidden>
            <GlobalOutlined />
          </span>
          <span className={styles.menuRowLabel}>{loc.word("School.settings.language", { default: "Language" })}</span>
          <Select
            size="small"
            variant="borderless"
            value={lang}
            popupMatchSelectWidth={false}
            onChange={(x) => selectLang(x as Lang)}
            options={loc.langs.map((x) => ({ value: x, label: loc.word(`language.${x}`) }))}
          />
        </div>
        {allowSwitch && deviceSize === "mobile" && (
          <div className={styles.menuSettingStack}>
            <span className={styles.menuSettingTitle}>{loc.word("School.theme.title", { default: "Appearance" })}</span>
            <ThemeSwitch block />
          </div>
        )}
      </div>

      <div className={styles.menuGroup}>
        <Row
          icon={<LogoutOutlined />}
          label={loc.word("Logout", { default: "Logout" })}
          onClick={done(() => logout())}
          danger
          testId="account-menu-logout"
        />
      </div>
    </div>
  );
};

/** Desktop: avatar (+ name) opening the account panel. */
export const AccountDropdown: React.FC<{ compact?: boolean; extraItems?: NavItem[] }> = ({ compact, extraItems }) => {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);

  return (
    <Dropdown
      open={open}
      onOpenChange={setOpen}
      trigger={["click"]}
      placement="bottomRight"
      popupRender={() => (
        <div className={styles.dropdownSurface}>
          <AccountPanel extraItems={extraItems} onDone={() => setOpen(false)} />
        </div>
      )}
    >
      <button type="button" className={styles.accountTrigger} data-testid="account-menu">
        {user && <UserAvatar user={user} size={36} color="var(--axl-primary)" />}
        {!compact && (
          <>
            <span className={styles.accountTriggerName}>{user?.fullName}</span>
            <DownOutlined className={styles.accountTriggerCaret} aria-hidden />
          </>
        )}
      </button>
    </Dropdown>
  );
};

/** Mobile: the "Profile" tab opens this bottom sheet. */
export const AccountSheet: React.FC<{ open: boolean; onClose: () => void; extraItems: NavItem[] }> = ({
  open,
  onClose,
  extraItems,
}) => (
  <Drawer
    open={open}
    onClose={onClose}
    placement="bottom"
    size="auto"
    closeIcon={null}
    title={null}
    rootClassName={styles.sheet}
    destroyOnHidden
  >
    <div className={styles.sheetHandle} aria-hidden />
    <AccountPanel extraItems={extraItems} onDone={onClose} />
  </Drawer>
);
